
import { useCallback, useEffect, useState, useMemo } from "react";
import debounce from "lodash/debounce";
import toast from "react-hot-toast";
import { createAdminNotification } from "../../utils/notificationService";
import { BiPackage } from "react-icons/bi";
import { IoCloseSharp } from "react-icons/io5";
import { useSelector, useDispatch } from "react-redux";
import { Button, Modal, ModalBody } from "reactstrap";
import {
  checkProductSkuUniqueAPI,
  getAllManufacturers,
  getAllCategories,
  getAllSubCategories,
} from "../../api/pagination";
import { AllCategories, AllSubCategories } from "../../store/actions/actions";
import { getAdminProductDetails } from "../../api/products.api";
import { newItemCondition, newItemSettings, productTypes } from "../../data";
import {
  generateCustomID,
  parseSpecs,
  saveData,
  uniqueID,
  uploadImage,
  uploadProductImage,
} from "../../firebase/utility";
import GeneralButton from "../button/GeneralButton";
import Separator from "../Separator";
import AddNewManufacturer from "./AddNewManufacturer";
import Step1Identifiers from "./productSteps/Step1Identifiers";
import Step2Categorization from "./productSteps/Step2Categorization";
import Step3PricingStock from "./productSteps/Step3PricingStock";
import Step4LogisticsMedia from "./productSteps/Step4LogisticsMedia";
import Step5Specifications from "./productSteps/Step5Specifications";
const CreateNewItem = ({
  modal,
  toggle,
  newItem,
  setNewItem,
  productSlug,
  gettingProducts,
  selectedProductProp,
}) => {
  const dispatch = useDispatch();
  const [selectedProduct, setSelectedProduct] = useState(null);

  const { all_manufacturers, all_categories, all_subCategories } = useSelector(
    (state) => state.redux,
  );
  const { user } = useSelector((state) => state.auth);

  const [mfg, setMfg] = useState(null);
  const [mfgModal, setMfgModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [searchedMfg, setSearchMfg] = useState([]);
  const [isSameSku, setIsSameSku] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);

  useEffect(() => {
    if (!all_categories || all_categories.length === 0) {
      getAllCategories(1, "", 2000, (res) => {
        if (res?.status === 200 && (res?.data?.data || res?.data)) {
          dispatch(AllCategories(res.data.data || res.data));
        }
      });
    }
    if (!all_subCategories || all_subCategories.length === 0) {
      getAllSubCategories(1, "", 2000, (res) => {
        if (res?.status === 200 && (res?.data?.data || res?.data)) {
          dispatch(AllSubCategories(res.data.data || res.data));
        }
      });
    }
  }, [all_categories, all_subCategories, dispatch]);

  useEffect(() => {
    if (selectedProduct?.sku && newItem?.sku) {
      setIsSameSku(selectedProduct.sku === newItem.sku);
    } else {
      setIsSameSku(false);
    }
  }, [selectedProduct?.sku, newItem?.sku]);

  const addNewItem = (status) => {
    setLoading(true);

    const {
      sku,
      length,
      height,
      width,
      weight,
      mfg_name,
      item_title,
      price,
      productType,
      long_description,
      description,
      ...rest
    } = newItem;

    const excludeId = selectedProduct?._id ?? "";

    const proceedAfterSkuCheck = async () => {
      // ---------------- Required field validation ----------------
      const requiredFields = [
        { key: "sku", label: "SKU" },
        { key: "item_title", label: "Title" },
        { key: "length", label: "Length" },
        { key: "height", label: "Height" },
        { key: "width", label: "Width" },
        { key: "weight", label: "Weight" },
        { key: "price", label: "Price" },
        { key: "productType", label: "Product Type" },
        { key: "mfg", label: "Manufacturer" },
        { key: "stc_quantity", label: "Stock Quantity" },
        { key: "stc_cost", label: "Stock Cost" },
        { key: "item_upc", label: "UPC" },
        { key: "imageUrl", label: "Image URL" },
        { key: "mfg_condition", label: "Condition" },
      ];

      requiredFields.push(
        { key: "selectedCategory", label: "Category / Sub Category" },
      );

      const missing = requiredFields.filter((f) => {
        if (f.key === "mfg") return !mfg?.name;
        if (f.key === "selectedCategory") return !selectedCategory;
        if (f.key === "imageUrl" && newItem?.tempImageFile) return false;
        const val = newItem[f.key];
        if (val === undefined || val === null) return true;
        if (typeof val === "string") return val.trim() === "";
        return false;
      });

      if (missing.length > 0) {
        const names = missing.map((m) => m.label).join(", ");
        toast.error(`Please fill required fields: ${names}`);
        setLoading(false);
        return;
      }

      // ---------------- Image Upload ----------------
      let finalImageUrl = rest.imageUrl || "";
      if (newItem?.tempImageFile) {
        try {
          const uid = uniqueID();
          const imageName = `tmu-${uid}`;
          finalImageUrl = await uploadProductImage(
            newItem.tempImageFile,
            imageName,
          );
        } catch (error) {
          toast.error("Failed to upload image. Please try again.");
          setLoading(false);
          return;
        }
      }

      // Build the base data common to both create and update
      const commonData = {
        ...rest,
        imageUrl: finalImageUrl,
        tempImageFile: null,
        name: item_title,
        dimensions: {
          length: typeof length === "string" ? length.trim() : (length ?? ""),
          height: typeof height === "string" ? height.trim() : (height ?? ""),
          width: typeof width === "string" ? width.trim() : (width ?? ""),
        },
        price,
        status,
        weight,
        sku,
        mfg_name: mfg?.name ?? "",
        mfgDetails: mfg ?? {},
        mfg_id: mfg?._id ?? "",
        category: selectedCategory,
        subCategory: selectedSubCategory,
        productType,
        long_description: long_description ?? "",
        description: description ?? "",
        item_specification: parseSpecs(newItem.item_specification),
        showPriceToGuests: Boolean(newItem?.showPriceToGuests),
        hidePriceFromGuests: Boolean(newItem?.hidePriceFromGuests),
      };

      // Audit actor snapshot
      const actorSnapshot = {
        id: user?._id ?? "",
        name: user?.username ?? "",
        email: user?.email ?? "",
      };

      const isUpdating = newItem?.id || selectedProduct?._id;

      if (isUpdating) {
        // -------- Update Case --------
        const obj = {
          ...commonData,
          _id: selectedProduct?._id ?? uniqueID(),
          id: newItem?.id || selectedProduct?.id,
          // Preserve original creation info
          date_created: selectedProduct?.date_created ?? Date.now(),
          created_by: selectedProduct?.created_by ?? null,
          // Stamp who updated and when
          updated_at: Date.now(),
          updated_by: { ...actorSnapshot, at: Date.now() },
        };
        saveData("Products", obj._id, obj)
          .then((res) => {
            if (res) {
              toast.success(`Product of SKU ${obj?.sku} updated successfully!`);
              createAdminNotification({
                title: "Product Updated",
                message: `Product SKU ${obj?.sku} was updated by ${user?.username || "Admin"}`,
                category: "product",
                entityId: obj?.sku,
                targetUrl: "/admin/items",
                actor: user,
              }).catch(console.error);
              gettingProducts();
              toggle();
              setNewItem({});
            }
          })
          .catch((error) => toast.error(error?.message))
          .finally(() => setLoading(false));
      } else {
        // -------- Add Case --------
        generateCustomID("Products", "id").then((res) => {
          if (res) {
            const obj = {
              ...commonData,
              _id: selectedProduct?._id ?? uniqueID(),
              id: res,
              // Stamp who created and when
              date_created: Date.now(),
              created_at: Date.now(),
              created_by: { ...actorSnapshot, at: Date.now() },
              updated_at: null,
              updated_by: null,
            };
            saveData("Products", obj._id, obj)
              .then((res) => {
                if (res) {
                  toast.success(
                    `Product of SKU ${obj?.sku} saved successfully!`,
                  );
                  createAdminNotification({
                    title: "Product Created",
                    message: `Product SKU ${obj?.sku} was created by ${user?.username || "Admin"}`,
                    category: "product",
                    entityId: obj?.sku,
                    targetUrl: "/admin/items",
                    actor: user,
                  }).catch(console.error);
                  gettingProducts();
                  toggle();
                  setNewItem({});
                }
              })
              .catch((error) => toast.error(error?.message))
              .finally(() => setLoading(false));
          }
        });
      }
    };

    const isUpdating = newItem?.id || selectedProduct?._id;
    if (isUpdating) {
      // No need to call API when updating
      proceedAfterSkuCheck();
    } else {
      // Check uniqueness first when creating new item
      checkProductSkuUniqueAPI(sku, excludeId, (result) => {
        if (result?.status !== 200) {
          toast.error("Item with the same SKU already exists");
          setLoading(false);
        } else {
          proceedAfterSkuCheck();
        }
      });
    }
  };

  const fetchManufacturersDebounced = useCallback(
    debounce((searchText) => {
      getAllManufacturers(1, searchText, 50, (res) => {
        if (res) {
          setSearchMfg(res?.data?.data);
        }
        setDataLoading(false);
      });
    }, 500),
    []
  );

  const handleManufacturerSearch = (searchText) => {
    setDataLoading(true);
    fetchManufacturersDebounced(searchText);
  };
  const handleMfg = (val) => {
    let source = searchedMfg?.length > 0 ? searchedMfg : all_manufacturers;
    let res = source?.find((i) => i?._id === val);
    setMfg(res);
  };

  useEffect(() => {
    if (selectedCategory && selectedCategory.categoryType?.toLowerCase() !== newItem?.productType?.toLowerCase()) {
      setSelectedCategory(null);
      setSelectedSubCategory(null);
    }
  }, [newItem?.productType]);


  const handleSubmitData = async (obj, file, setData) => {
    setLoading(true);
    let data = {
      _id: uniqueID(),
      ...obj,
      createdAt: Date.parse(new Date()),
      id: await generateCustomID("Manufacturers", "id"),
    };
    let losFile;
    if (file != undefined && typeof file === "object") {
      losFile = await uploadImage(file, "manufacturers");
      if (losFile) {
        data.loaLos = losFile;
      }
    } else {
      data.loaLos = false;
    }
    saveData("Manufacturers", data?._id, data).then((res) => {
      if (res) {
        toast.success(`Manufacturer ${data?.name} Saved Successfully`);
        setData(null);
        setMfgModal(false);
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    let slug = productSlug || selectedProductProp?.slug;
    if (slug) {
      setFetchingDetails(true);
      getAdminProductDetails(slug, (res) => {
        if (res.status === 200) {
          setSelectedProduct(res?.data?.data);
          let data = res?.data?.data?.mfgDetails;
          let categorySelected = res?.data?.data?.category;
          let subCategorySelected = res?.data?.data?.subCategory;
          setSelectedCategory(categorySelected);
          setSelectedSubCategory(subCategorySelected);
          setMfg(data);

          const fetchedProduct = res?.data?.data;
          setNewItem((prev) => ({
            ...prev,
            ...fetchedProduct,
            long_description: fetchedProduct?.long_description ?? prev?.long_description ?? "",
            description: fetchedProduct?.description ?? prev?.description ?? "",
            item_specification: fetchedProduct?.item_specification ?? prev?.item_specification ?? {},
            visible: fetchedProduct?.visible ?? true,
            showPriceToGuests: fetchedProduct?.showPriceToGuests ?? false,
            hidePriceFromGuests: fetchedProduct?.hidePriceFromGuests ?? false,
          }));
        }
        setFetchingDetails(false);
      });
    } else if (selectedProductProp) {
      setFetchingDetails(false);
      setSelectedProduct(selectedProductProp);
      let data = selectedProductProp?.mfgDetails;
      let categorySelected = selectedProductProp?.category;
      let subCategorySelected = selectedProductProp?.subCategory;
      setSelectedCategory(categorySelected);
      setSelectedSubCategory(subCategorySelected);
      setMfg(data);
      setNewItem((prev) => ({
        ...prev,
        ...selectedProductProp,
        showPriceToGuests: selectedProductProp?.showPriceToGuests ?? false,
        hidePriceFromGuests: selectedProductProp?.hidePriceFromGuests ?? false,
      }));
    } else {
      setFetchingDetails(false);
    }
  }, [productSlug, newItem?.id, selectedProductProp]);

  return (
    <>
      <AddNewManufacturer
        modal={mfgModal}
        toggle={() => setMfgModal((prev) => !prev)}
        handleSubmitData={handleSubmitData}
        loading={loading}
      />
      <Modal
        isOpen={modal}
        toggle={toggle}
        centered
        size="xl"
        onOpened={() => {
          if (!newItem?.id && !selectedProductProp?._id) {
            setSelectedCategory(null);
            setSelectedSubCategory(null);
            setMfg(null);
          }
        }}
        onClosed={() => {
          setNewItem(null);
          setDraftLoading(false);
          setFetchingDetails(false);
        }}
      >
        <ModalBody className="p-0 border-0">
          <div className="modern-modal-header d-flex justify-content-between align-items-center p-4 border-bottom bg-light rounded-top">
            <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
              <BiPackage className="text-primary" size={20} /> {(newItem?.id || selectedProduct?._id) ? "Edit Item" : "Create New Item"}
            </h5>
            <IoCloseSharp
              size={24}
              className="text-muted pointer hover-opacity-75"
              onClick={toggle}
              style={{ cursor: "pointer" }}
            />
          </div>
          <div className="p-4">
            {fetchingDetails ? (
              <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="mt-3 text-muted">Fetching product details...</h5>
              </div>
            ) : (
              <>
                <div
                  className="step-content mb-4"
                >
                  <Step1Identifiers newItem={newItem} setNewItem={setNewItem} />
                  <Step2Categorization
                    newItem={newItem}
                    setNewItem={setNewItem}
                    mfg={mfg}
                    handleMfg={handleMfg}
                    handleManufacturerSearch={handleManufacturerSearch}
                    searchedMfg={searchedMfg}
                    all_manufacturers={all_manufacturers}
                    dataLoading={dataLoading}
                    productTypes={productTypes}
                    all_categories={all_categories}
                    all_subCategories={all_subCategories}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    selectedSubCategory={selectedSubCategory}
                    setSelectedSubCategory={setSelectedSubCategory}
                    newItemCondition={newItemCondition}
                    setMfgModal={setMfgModal}
                  />
                  <Step3PricingStock newItem={newItem} setNewItem={setNewItem} />
                  <Step4LogisticsMedia
                    newItem={newItem}
                    setNewItem={setNewItem}
                    newItemSettings={newItemSettings}
                  />
                  <Step5Specifications
                    newItem={newItem}
                    setNewItem={setNewItem}
                  />
                </div>

                <Separator />
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                    {selectedProduct?.created_by?.name && (
                      <div>
                        <span className="fw-semibold">Created by:</span> {selectedProduct.created_by.name}
                        {selectedProduct.created_by.at && ` on ${new Date(selectedProduct.created_by.at).toLocaleString()}`}
                      </div>
                    )}
                    {selectedProduct?.updated_by?.name && (
                      <div>
                        <span className="fw-semibold">Last updated by:</span> {selectedProduct.updated_by.name}
                        {selectedProduct.updated_by.at && ` on ${new Date(selectedProduct.updated_by.at).toLocaleString()}`}
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      color="secondary"
                      outline
                      onClick={toggle}
                      style={{ width: "120px" }}
                    >
                      Cancel
                    </Button>
                    <GeneralButton
                      title={(newItem?.id || selectedProduct?._id) ? "Update Product" : "Save Product"}
                      className={"blue-button"}
                      width={"180px"}
                      loading={loading}
                      disabled={loading}
                      handleClick={() => addNewItem("publish")}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </ModalBody>
      </Modal>


    </>
  );
};
export default CreateNewItem;
